import { useCallback, useRef, useState } from 'react';
import { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' | null;

function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState<Direction>(null);
  const lastValue = useRef({ x: 0, y: 0 });
  const isStopListening = useRef(false);

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (isStopListening.current) return;

    const { x, y } = e.nativeEvent.contentOffset;
    const { contentSize, layoutMeasurement } = e.nativeEvent;

    if (x !== lastValue.current.x) {
      setScrollDirection(x > lastValue.current.x ? 'RIGHT' : 'LEFT');
      lastValue.current.x = x;

      return;
    }

    setScrollDirection(
      // Web 上會出現初始值與新位置相同數字的情形，故以大於等於來判斷。
      // IOS 的操作，在上滑至頂部時會有慣性的回彈效果，ｙ會抓取到負數值，而y小於零的值不應視為下滑。
      // 同理，當下滑至底部時其回彈效果，不應視為上滑。
      y >= lastValue.current.y
        ? y > 0
          ? 'DOWN'
          : 'UP'
        : y + layoutMeasurement.height >= contentSize.height
        ? 'DOWN'
        : 'UP'
    );

    // 部分裝置操作上相較敏感，event觸發較頻繁，為避免抖動產生的細微誤差，所以把數值差距拉開至少 1 個像素。
    if (lastValue.current.y <= y) {
      lastValue.current.y = y - 1;
    } else {
      lastValue.current.y = y + 1;
    }
  }, []);

  const stopListening = useCallback(() => {
    isStopListening.current = true;
  }, []);

  return {
    scrollDirection,
    isScrollingUp: scrollDirection === 'UP',
    isScrollingDown: scrollDirection === 'DOWN',
    isScrollingLeft: scrollDirection === 'LEFT',
    isScrollingRight: scrollDirection === 'RIGHT',
    onScroll,
    stopScrollListening: stopListening,
  };
}

export default useScrollDirection;
